import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Bot, MessageSquare, Tag, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AICompanion() {
  const { clientId } = useParams();
  const [config, setConfig] = useState(null);
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/clients/${clientId}/companion`, { withCredentials: true }),
      axios.get(`${API}/clients/${clientId}`, { withCredentials: true })
    ]).then(([compRes, clientRes]) => {
      setConfig(compRes.data);
      setClientName(clientRes.data.business_name);
    }).catch(console.error).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin"></div></div>;
  if (!config || Object.keys(config).length === 0) return <div className="text-center py-20 text-[#9999B3]">No companion configured</div>;

  const typeLabels = { faq_bot: "FAQ Bot", intake_assistant: "Intake Assistant", planning_coach: "Planning Coach" };

  return (
    <div data-testid="ai-companion-page">
      <h1 className="text-2xl font-bold text-white mb-1">{clientName} — AI Companion</h1>
      <p className="text-[#9999B3] text-sm mb-6">Configuration and recent interactions</p>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Config */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-[#12121F] border border-[#252540] rounded-lg p-5">
            <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><Bot className="w-4 h-4 text-[#C9A84C]"/> Configuration</h3>
            <div className="space-y-3">
              <div>
                <p className="text-[#6B6B85] text-xs mb-1">Type</p>
                <p className="text-white text-sm font-medium">{typeLabels[config.type] || config.type}</p>
              </div>
              <div>
                <p className="text-[#6B6B85] text-xs mb-1">Greeting</p>
                <p className="text-[#C5C5D8] text-sm">{config.greeting || "—"}</p>
              </div>
              <div>
                <p className="text-[#6B6B85] text-xs mb-1">Topics</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {config.topics?.map((t, i) => (
                    <span key={i} className="text-xs bg-[#C9A84C]/10 text-[#C9A84C] px-2.5 py-1 rounded-full border border-[#C9A84C]/20" data-testid={`topic-${i}`}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-[#12121F] border border-[#252540] rounded-lg p-5">
            <h3 className="text-white font-semibold text-sm mb-2">Stats</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-[#6B6B85] text-xs">Total Chats</p><p className="text-white text-lg font-bold">{config.interactions?.length || 0}</p></div>
              <div><p className="text-[#6B6B85] text-xs">Topics</p><p className="text-white text-lg font-bold">{config.topics?.length || 0}</p></div>
            </div>
          </div>
        </div>

        {/* Recent Interactions */}
        <div className="lg:col-span-2 bg-[#12121F] border border-[#252540] rounded-lg p-5">
          <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#C9A84C]"/> Recent Interactions</h3>
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
            {config.interactions?.length > 0 ? config.interactions.map((msg, i) => (
              <div key={i} className="border-b border-[#252540] pb-4 last:border-0 last:pb-0" data-testid={`interaction-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3 h-3 text-[#6B6B85]"/>
                  <span className="text-[#6B6B85] text-xs">{new Date(msg.timestamp).toLocaleString()}</span>
                </div>
                {/* User message */}
                <div className="flex justify-end mb-2">
                  <div className="bg-[#252540] text-[#C5C5D8] text-sm px-3 py-2 rounded-lg rounded-tr-none max-w-[80%]">
                    {msg.user_msg}
                  </div>
                </div>
                {/* Bot response */}
                <div className="flex justify-start">
                  <div className="bg-[#C9A84C]/10 text-[#C5C5D8] text-sm px-3 py-2 rounded-lg rounded-tl-none max-w-[80%] border border-[#C9A84C]/20">
                    <span className="text-[#C9A84C] text-xs font-semibold block mb-1">AI Companion</span>
                    {msg.bot_msg}
                  </div>
                </div>
              </div>
            )) : <p className="text-[#9999B3] text-sm text-center py-8">No interactions yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
